import {
  ComboboxItem,
  Group,
  Image,
  Loader,
  Select,
  SelectProps,
  Text,
  ThemeIcon
} from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import ImagePlaceholder from '@/assets/datasets/dataset.png'
import { Dataset } from '@/types/dataset'

const INPUT_HEIGHT = 40

const THUMBNAIL_WIDTH_INPUT = 32
const THUMBNAIL_WIDTH_DROPDOWN = 48
const THUMBNAIL_HEIGHT_RATIO = 0.75

const MAX_DROPDOWN_HEIGHT = 400

type DatasetThumbnailProps = {
  imageUrl?: string
  width?: number
  height?: number
}

const DatasetThumbnail = ({
  imageUrl,
  width,
  height
}: DatasetThumbnailProps) => {
  return (
    <Image
      w={width}
      h={height}
      fit="cover"
      src={imageUrl || ImagePlaceholder}
      fallbackSrc={ImagePlaceholder}
      radius="xs"
      styles={(theme) => ({
        root: {
          outline: `1px solid ${theme.colors.dark[3]}`
        }
      })}
    />
  )
}

type SelectOptionProps = {
  name: string
  isChecked: boolean
  imageUrl?: string
}

const SelectOption = ({ name, isChecked, imageUrl }: SelectOptionProps) => {
  return (
    <Group flex="1" align="start" wrap="nowrap" py={4}>
      <Group wrap="nowrap" gap="xs" align="center">
        <DatasetThumbnail
          imageUrl={imageUrl}
          width={THUMBNAIL_WIDTH_DROPDOWN}
          height={THUMBNAIL_WIDTH_DROPDOWN * THUMBNAIL_HEIGHT_RATIO}
        />

        <Text size="sm" fw={500}>
          {name}
        </Text>
      </Group>

      {isChecked && (
        <ThemeIcon
          color="green"
          size="xs"
          style={{ marginInlineStart: 'auto' }}
        >
          <IconCheck style={{ width: '80%', height: '80%' }} />
        </ThemeIcon>
      )}
    </Group>
  )
}

type DatasetSelectProps = SelectProps & {
  datasets: Dataset[]
  isLoading?: boolean
}

export const DatasetSelect = (props: DatasetSelectProps) => {
  const { datasets, isLoading, ...selectProps } = props

  const datasetOptions = useMemo(
    () =>
      datasets.map((dataset) => ({
        value: dataset.id,
        label: dataset.name,
        dataset: dataset
      })),
    [datasets]
  )

  const selectedDataset = useMemo(
    () => datasetOptions.find((option) => option.value === selectProps.value),
    [datasetOptions, selectProps.value]
  )

  return (
    <Select
      data={datasetOptions}
      allowDeselect={false}
      maxDropdownHeight={MAX_DROPDOWN_HEIGHT}
      leftSectionWidth={THUMBNAIL_WIDTH_INPUT + 20}
      leftSection={
        selectedDataset && (
          <DatasetThumbnail
            imageUrl={selectedDataset.dataset.thumbnail || undefined}
            width={THUMBNAIL_WIDTH_INPUT}
            height={THUMBNAIL_WIDTH_INPUT * THUMBNAIL_HEIGHT_RATIO}
          />
        )
      }
      rightSection={isLoading ? <Loader size="xs" /> : null}
      nothingFoundMessage={<FormattedMessage id="datasets.noItemsFound" />}
      styles={{
        input: {
          height: INPUT_HEIGHT
        }
      }}
      renderOption={({ option, checked }) => {
        const { dataset } = option as ComboboxItem & { dataset: Dataset }

        return (
          <SelectOption
            name={option.label}
            isChecked={checked || false}
            imageUrl={dataset.thumbnail || undefined}
          />
        )
      }}
      {...selectProps}
    />
  )
}
